// Phase 285 — Pitch Funnel Heat Map
// Glass-box view of every pitch dispatched in the rolling window.
// Each row shows the chain: Fired → Delivered → Opened → Clicked → Booked.
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Mail, Send, CheckCircle2, Eye, MousePointer, CalendarCheck,
  AlertOctagon, RefreshCw, Activity, Bot,
} from 'lucide-react';

const formatTs = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const STAGES = [
  { key: 'fired', label: 'Fired', tsKey: 'pitch_sent_at', Icon: Send, tone: 'text-slate-500 border-slate-300' },
  { key: 'delivered', label: 'Delivered', tsKey: 'pitch_delivered_at', Icon: CheckCircle2, tone: 'text-blue-600 border-blue-300' },
  { key: 'opened', label: 'Opened', tsKey: 'pitch_opened_at', Icon: Eye, tone: 'text-indigo-600 border-indigo-300' },
  { key: 'clicked', label: 'Clicked', tsKey: 'pitch_clicked_at', Icon: MousePointer, tone: 'text-purple-600 border-purple-300' },
  { key: 'booked', label: 'Booked', tsKey: 'pitch_converted_at', Icon: CalendarCheck, tone: 'text-emerald-600 border-emerald-300' },
];

const StageChip = ({ ts, label, Icon, tone }) => {
  const hit = Boolean(ts);
  return (
    <div
      title={ts ? `${label}: ${formatTs(ts)}` : `${label}: pending`}
      className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap transition-colors ${
        hit
          ? `bg-white dark:bg-slate-900 ${tone}`
          : 'border-dashed border-slate-200 dark:border-slate-700 text-muted-foreground/40'
      }`}
    >
      <Icon className="h-2.5 w-2.5" />
      {label}
    </div>
  );
};

export default function PitchFunnelHeatMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState('24');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/super-admin/analytics/pitch-timeline', { params: { hours } });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load pitch timeline');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  const counts = data?.stage_counts || {};
  const timeline = data?.timeline || [];

  return (
    <Card data-testid="pitch-funnel-heatmap">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-purple-700 dark:text-purple-400">
              <Activity className="w-3 h-3" /> Pitch Funnel Heat Map
            </div>
            <h3 className="text-base font-display font-semibold mt-1 text-slate-900 dark:text-slate-100">
              Glass-box attribution · last {data?.window_hours || hours}h
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Every pitch email plotted from dispatch to booking. Resend webhooks fill in delivery, open, and click in real time.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={hours} onValueChange={setHours}>
              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="heatmap-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">Last 6 hours</SelectItem>
                <SelectItem value="24">Last 24 hours</SelectItem>
                <SelectItem value="72">Last 3 days</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
                <SelectItem value="336">Last 14 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="heatmap-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Stage totals row */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            { key: 'fired', label: 'Fired', Icon: Send, color: 'text-slate-700' },
            { key: 'delivered', label: 'Delivered', Icon: CheckCircle2, color: 'text-blue-600' },
            { key: 'opened', label: 'Opened', Icon: Eye, color: 'text-indigo-600' },
            { key: 'clicked', label: 'Clicked', Icon: MousePointer, color: 'text-purple-600' },
            { key: 'booked', label: 'Booked', Icon: CalendarCheck, color: 'text-emerald-600' },
            { key: 'bounced', label: 'Bounced', Icon: AlertOctagon, color: 'text-rose-500' },
          ].map(({ key, label, Icon, color }) => (
            <div
              key={key}
              data-testid={`heatmap-stage-${key}`}
              className="rounded-lg border p-2.5 bg-white dark:bg-slate-900/40"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">
                <Icon className={`w-3 h-3 ${color}`} />
                {label}
              </div>
              <p className={`text-xl font-display font-bold tabular-nums mt-1 ${color}`}>
                {counts[key] ?? 0}
              </p>
            </div>
          ))}
        </div>

        {/* Timeline rows */}
        <div className="space-y-2">
          {loading && (
            <>
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </>
          )}
          {!loading && timeline.length === 0 && (
            <div className="text-center py-10 text-sm text-muted-foreground" data-testid="heatmap-empty">
              No pitches in this window yet. Fire one from the visitor table or enable Drift-to-Pitch above.
            </div>
          )}
          {!loading && timeline.map((row) => {
            const visitorLabel = row.name || (row.email ? row.email.split('@')[0] : 'Anonymous');
            const isAuto = row.pitch_sent_by === 'auto_drift_rule';
            return (
              <div
                key={row.session_id}
                data-testid={`heatmap-row-${row.session_id}`}
                className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900/40 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[200px]">
                        {visitorLabel}
                      </p>
                      {isAuto ? (
                        <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-violet-300 text-violet-700 dark:text-violet-300">
                          <Bot className="w-2.5 h-2.5 mr-1" /> Auto
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-slate-300 text-slate-600">
                          Manual
                        </Badge>
                      )}
                      {row.email && (
                        <span className="text-[11px] text-muted-foreground truncate">{row.email}</span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Fired {formatTs(row.pitch_sent_at)}
                      {row.location?.city && row.location.city !== '—' ? ` · ${row.location.city}` : ''}
                    </p>
                  </div>
                </div>

                {/* Chain of stage chips */}
                <div className="flex items-center gap-1.5 mt-2.5 flex-wrap" data-testid={`heatmap-stages-${row.session_id}`}>
                  {STAGES.map(({ key, label, tsKey, Icon, tone }) => (
                    <StageChip
                      key={key}
                      ts={row[tsKey]}
                      label={label}
                      Icon={Icon}
                      tone={tone}
                    />
                  ))}
                  {row.pitch_bounced_at && (
                    <StageChip
                      ts={row.pitch_bounced_at}
                      label="Bounced"
                      Icon={AlertOctagon}
                      tone="text-rose-600 border-rose-300"
                    />
                  )}
                </div>

                {row.pitch_click_target && (
                  <p className="text-[10px] text-muted-foreground mt-2 truncate">
                    <MousePointer className="w-2.5 h-2.5 inline mr-1" />
                    Clicked: <code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">{row.pitch_click_target}</code>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
